/**
 * Exam landing pages are data-driven: add an entry here and create a thin
 * route file that renders <ExamLanding exam={...} /> to ship a new exam page.
 */
export type ExamRoute =
  | "/jee"
  | "/neet"
  | "/cbse"
  | "/isc"
  | "/icse"
  | "/kcet"
  | "/mht-cet";

export interface ExamConfig {
  /** URL slug, e.g. "jee" → /jee */
  slug: string;
  /** Typed route path for <Link to={...}>. */
  route: ExamRoute;
  /** Short label used in navigation. */
  shortName: string;
  /** Full display name. */
  name: string;
  /** H1 of the landing page. */
  heading: string;
  /** Lead paragraph under the H1. */
  intro: string;
  /** SEO <title>. */
  title: string;
  /** SEO meta description. */
  description: string;
  /** Subjects this exam covers inside the app. */
  subjects: string[];
  /** Exam-specific reasons, all backed by features that exist today. */
  points: string[];
}

/** Where a visitor lands inside the app after signing in. */
export const APP_HOME = "/";

/** Remembers which exam page the visitor started from, across the auth flow. */
export const SELECTED_EXAM_KEY = "qm_selected_exam";

export function rememberExam(slug: string) {
  try {
    localStorage.setItem(SELECTED_EXAM_KEY, slug);
  } catch {
    /* storage disabled */
  }
}

export function getRememberedExam(): string | null {
  try {
    return localStorage.getItem(SELECTED_EXAM_KEY);
  } catch {
    return null;
  }
}

export const EXAMS: ExamConfig[] = [
  {
    slug: "jee",
    route: "/jee",
    shortName: "JEE",
    name: "JEE",
    heading: "JEE Preparation Tracker",
    intro:
      "Track your JEE preparation, revision, questions and tests in one place. Add chapters, tick lectures as you finish them, and let spaced revisions come back on schedule.",
    title: "JEE Study Planner & Preparation Tracker | The Quantum Minds",
    description:
      "Track your JEE syllabus, lectures, spaced revision, question practice and test scores in one free study planner from The Quantum Minds.",
    subjects: ["Physics", "Chemistry", "Mathematics"],
    points: [
      "Chapter-wise syllabus and lecture completion for Physics, Chemistry and Maths",
      "Spaced revision scheduled automatically from every completed lecture",
      "Unlimited tests with pre-test revision tasks and subject-wise marks",
      "Question practice targets tracked chapter by chapter",
    ],
  },
  {
    slug: "neet",
    route: "/neet",
    shortName: "NEET",
    name: "NEET",
    heading: "NEET Preparation Tracker",
    intro:
      "Track your NEET preparation across Physics, Chemistry and Biology — syllabus, lectures, revision, question practice and test scores in one place.",
    title: "NEET Study Planner & Preparation Tracker | The Quantum Minds",
    description:
      "Track your NEET syllabus, lectures, spaced revision, question practice and test scores in one free study planner from The Quantum Minds.",
    subjects: ["Physics", "Chemistry", "Biology"],
    points: [
      "Biology, Physics and Chemistry chapters tracked lecture by lecture",
      "Spaced revision so long-syllabus Biology stays retained",
      "Test tracker with subject-wise marks and score trends",
      "Biology tools and simulations in the free study toolkit",
    ],
  },
  {
    slug: "cbse",
    route: "/cbse",
    shortName: "CBSE",
    name: "CBSE",
    heading: "CBSE Study & Preparation Tracker",
    intro:
      "Keep your CBSE board preparation organised — add chapters per subject, tick lectures, revise on schedule and log every school or practice test.",
    title: "CBSE Study Planner & Preparation Tracker | The Quantum Minds",
    description:
      "Organise your CBSE board preparation: chapter tracking, lecture completion, spaced revision, question practice and test scores in one free planner.",
    subjects: ["Physics", "Chemistry", "Mathematics", "Biology"],
    points: [
      "Chapter-wise tracking for every subject you study",
      "Spaced revision before unit tests and boards",
      "Log each test with subject-wise marks",
      "Formula sheets, converters and simulations in the toolkit",
    ],
  },
  {
    slug: "isc",
    route: "/isc",
    shortName: "ISC",
    name: "ISC",
    heading: "ISC Study & Preparation Tracker",
    intro:
      "A calm, honest tracker for ISC preparation — syllabus completion, spaced revision, question practice and test performance, all in one place.",
    title: "ISC Study Planner & Preparation Tracker | The Quantum Minds",
    description:
      "Track your ISC syllabus, lectures, revision schedule, question practice and test scores with The Quantum Minds free study planner.",
    subjects: ["Physics", "Chemistry", "Mathematics", "Biology"],
    points: [
      "Real syllabus completion instead of a rough guess",
      "Automatic spaced revision for every finished lecture",
      "Unlimited tests with score history and averages",
      "Free toolkit, simulations and virtual labs",
    ],
  },
  {
    slug: "icse",
    route: "/icse",
    shortName: "ICSE",
    name: "ICSE",
    heading: "ICSE Study & Preparation Tracker",
    intro:
      "Track your ICSE preparation subject by subject — chapters, lectures, revision, question practice and tests, with progress you can actually see.",
    title: "ICSE Study Planner & Preparation Tracker | The Quantum Minds",
    description:
      "Track ICSE chapters, lectures, spaced revision, question practice and test scores in one free study planner from The Quantum Minds.",
    subjects: ["Physics", "Chemistry", "Mathematics", "Biology"],
    points: [
      "Chapter and lecture tracking for each subject",
      "Revision that returns on a schedule, not the night before",
      "Test tracker with subject-wise marks",
      "Periodic table, formulae and simulations, free to use",
    ],
  },
  {
    slug: "kcet",
    route: "/kcet",
    shortName: "KCET",
    name: "KCET",
    heading: "KCET Preparation Tracker",
    intro:
      "Prepare for KCET without losing track — add chapters, complete lectures, revise on a spaced schedule and log your test scores as you go.",
    title: "KCET Study Planner & Preparation Tracker | The Quantum Minds",
    description:
      "Track your KCET syllabus, lectures, revision, question practice and test scores in one free study planner from The Quantum Minds.",
    subjects: ["Physics", "Chemistry", "Mathematics", "Biology"],
    points: [
      "Syllabus completion tracked chapter by chapter",
      "Spaced revision generated from your own progress",
      "Practice targets and question logging per chapter",
      "Score history, averages and weak-subject insights",
    ],
  },
  {
    slug: "mht-cet",
    route: "/mht-cet",
    shortName: "MHT CET",
    name: "MHT CET",
    heading: "MHT CET Preparation Tracker",
    intro:
      "Track MHT CET preparation end to end — chapters, lectures, spaced revision, question practice and every test you take.",
    title: "MHT CET Study Planner & Preparation Tracker | The Quantum Minds",
    description:
      "Track your MHT CET syllabus, lectures, revision schedule, question practice and test scores with The Quantum Minds free study planner.",
    subjects: ["Physics", "Chemistry", "Mathematics", "Biology"],
    points: [
      "Chapter-wise progress across all your subjects",
      "Automatic spaced revision for finished lectures",
      "Unlimited tests with subject-wise marks and trends",
      "Free toolkit, simulations and virtual labs",
    ],
  },
];

export function getExam(slug: string): ExamConfig {
  const exam = EXAMS.find((e) => e.slug === slug);
  if (!exam) throw new Error(`Unknown exam: ${slug}`);
  return exam;
}
